import React, { useState, useEffect, useMemo } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles, useTheme } from '@material-ui/styles';
import {
  Button,
  Dialog,
  Typography,
  colors,
  IconButton,
  useMediaQuery,
  Box,
  TextField
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PublishIcon from '@material-ui/icons/Publish';
import PerfectScrollbar from 'react-perfect-scrollbar';
import Repositories from 'services/repositories';
import tree from 'utils/tree'
import { Folders } from './components';

const useStyles = makeStyles(theme => ({
  root: {
    overflow: 'hidden',
  },
  header: {
    position: 'sticky',
    top: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2)
  },
  main: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'auto'
  },
  divider: {
    margin: theme.spacing(1, 0)
  },
  actions: {
    position: 'sticky',
    bottom: 0,
    backgroundColor: colors.grey[100],
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between'
  },
  flexRow: {
    display: 'flex',
    alignItems: 'center'
  },
  checkbox: {
    paddingTop: 0,
    paddingBottom: 0
  },
  startButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
  inputPadding: {
    padding: theme.spacing(0, 1)
  },
  clearButton: {
    position: 'absolute',
    right: 10,
    top: 10,
    backgroundColor: '#fff'
  }
}));

const DatasetModal = props => {
  const { open, onClose, onSubmit, className } = props;

  const theme = useTheme();
  const classes = useStyles();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const [applicationDatasets, setApplicationDatasets] = useState({folders: [], datasets: []});
  const [checked, setChecked] = useState([]);
  const [searchText, setSearchText] = useState('');

  const clearSearchInput = () => setSearchText('');

  const handleToggle = ({ id, name }) => {
    const currentIndex = checked.findIndex(el => el.id === id);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push({ id, name });
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleClose = () => {
    onClose();
    setChecked([]);
  };

  useEffect(() => {
    if (open) {
      setChecked([]);
      Repositories.getApplicationDatasets()
        .then(({ data }) => setApplicationDatasets(data))
        .catch(console.log);
    }
  }, [open])

  const filterEmptyFolders = (folder) => {
    if(folder.children && Array.isArray(folder.children)) {
      folder.children = folder.children.filter(filterEmptyFolders)
      return folder.children.length > 0;
    }
    return true;
  }

  const filterdFolders = useMemo(() => {

    const filteredDatasets = applicationDatasets.datasets.filter((dataset) => {
      return (searchText.length > 0) ? dataset.name.toLowerCase().includes(searchText.toLowerCase()) : true;
    })

    const folders = tree(applicationDatasets.folders, filteredDatasets)
      .filter(filterEmptyFolders);

    return folders;
  
  }, [applicationDatasets, searchText]);

  return (
    <Dialog
      classes={{ paper: clsx(classes.root, className) }}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
    >
      <div className={classes.header}>
        <Typography
          variant="h3"
        >
          Select Datasets
        </Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <PerfectScrollbar className={classes.main}>
        <Box p={2} width={'100%'} display={'block'} position={'relative'}>
          <TextField
            placeholder='Search Dataset'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)} 
            fullWidth 
          />
          {searchText.length > 0 && 
            <IconButton
              size='small'
              className={classes.clearButton}
              onClick={clearSearchInput}
            >
              <CloseIcon />
            </IconButton>
          }
        </Box>
        {filterdFolders.length === 0 && 
          <Box p={1} width={'100%'}>
            <Typography variant='h5' align='center'>
              No records found
            </Typography>
          </Box>
        }
        {
          searchText.length > 0 ?
            <Folders
              key={0}
              open
              checked={checked}
              folders={filterdFolders}
              onFolderOpen={handleToggle}
            />
            :
            <Folders
              key={1}
              checked={checked}
              folders={filterdFolders}
              onFolderOpen={handleToggle}
            />
        }
      </PerfectScrollbar>
      <div className={classes.actions}>
        <div className={classes.flexRow}>
          {Boolean(checked.length) &&
            <Typography
              color="textSecondary"
              variant="subtitle1"
            >
              {checked.length} selected
            </Typography>
          }
        </div>
        <Button
          color="primary"
          disabled={!checked.length}
          endIcon={<PublishIcon />}
          onClick={() => onSubmit({ toAdd: checked }, handleClose)}
          variant="contained"
        >
          Import
        </Button>
      </div>
    </Dialog>
  );
};

DatasetModal.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  open: PropTypes.bool
};

export default DatasetModal;
